import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import styles from './MessagesTab.module.scss';
import Loader from 'components/Loader/Loader';
import AttachFile from 'containers/AttachFile/AttachFile';

const MessagesTab = ({
  messages,
  loading,
  currentUser,
  otherUser,
  onSendMessage,
  onSendFile,
  hasMore,
  onLoadMore,
}) => {
  const [newMessage, setNewMessage] = useState('');
  const messagesAreaRef = useRef(null);
  const textareaRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const prevScrollHeightRef = useRef(0);

  const handleSend = () => {
    if (newMessage.trim() && onSendMessage) {
      onSendMessage(newMessage);
      setNewMessage('');
      // Reset textarea height after sending
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleMessageChange = (e) => {
    setNewMessage(e.target.value);
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const maxHeight = window.innerWidth <= 768 ? 120 : 200;
      const newHeight = Math.min(textareaRef.current.scrollHeight, maxHeight);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const scrollToBottom = () => {
    if (messagesAreaRef.current) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    const messagesArea = messagesAreaRef.current;
    if (!messagesArea || !messages) return;

    const currentMessageCount = messages.length;
    const prevMessageCount = prevMessageCountRef.current;

    // If messages increased and we had previous messages, check if we're loading more
    if (currentMessageCount > prevMessageCount && prevMessageCount > 0) {
      const prevScrollHeight = prevScrollHeightRef.current;
      const currentScrollHeight = messagesArea.scrollHeight;

      // If scroll height increased significantly, we loaded older messages
      // Preserve scroll position by adjusting for the new content height
      if (currentScrollHeight > prevScrollHeight) {
        const heightDifference = currentScrollHeight - prevScrollHeight;
        messagesArea.scrollTop = messagesArea.scrollTop + heightDifference;
      } else {
        // New message at bottom, scroll to bottom
        scrollToBottom();
      }
    } else if (prevMessageCount === 0) {
      // Initial load, scroll to bottom
      scrollToBottom();
    }

    // Update refs
    prevMessageCountRef.current = currentMessageCount;
    prevScrollHeightRef.current = messagesArea.scrollHeight;
  }, [messages]);

  // Infinite scroll - load more when scrolling near top
  useEffect(() => {
    const messagesArea = messagesAreaRef.current;
    if (!messagesArea || !hasMore || loading) return;

    const handleScroll = () => {
      // Check if scrolled near top (within 100px)
      if (messagesArea.scrollTop < 100 && hasMore && !loading) {
        onLoadMore && onLoadMore();
      }
    };

    messagesArea.addEventListener('scroll', handleScroll);
    return () => messagesArea.removeEventListener('scroll', handleScroll);
  }, [hasMore, loading, onLoadMore]);

  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentDate = null;
    let currentGroup = [];

    messages.forEach((message) => {
      const messageDate = moment(message.timestamp).format('YYYY-MM-DD');

      if (messageDate !== currentDate) {
        if (currentGroup.length > 0) {
          groups.push({ date: currentDate, messages: currentGroup });
        }
        currentDate = messageDate;
        currentGroup = [message];
      } else {
        currentGroup.push(message);
      }
    });

    if (currentGroup.length > 0) {
      groups.push({ date: currentDate, messages: currentGroup });
    }

    return groups;
  };

  return (
    <div className={styles.messagesContainer}>
      {/* Messages Area */}
      <div className={styles.messagesArea} ref={messagesAreaRef}>
        {/* Loading indicator at top when loading more */}
        {loading && messages && messages.length > 0 && <Loader loading />}

        {loading && (!messages || messages.length === 0) && <Loader loading />}
        {!loading && (!messages || messages.length === 0) ? (
          <div className={styles.emptyState}>
            <p>No messages yet</p>
            <p className={styles.emptyStateSubtext}>
              Start a conversation with{' '}
              {otherUser?.displayname ||
                otherUser?.username ||
                'this applicant'}
            </p>
          </div>
        ) : (
          groupMessagesByDate(messages).map((group) => (
            <React.Fragment key={group.date}>
              {/* Date Header */}
              <div className={styles.dateHeader}>
                <span className={styles.dateHeaderText}>
                  {moment(group.messages[0].timestamp).format('DD MMM YYYY')}
                </span>
              </div>

              {/* Messages for this date */}
              {group.messages.map((message) => {
                const isMe = message.sender_id === currentUser?.id;
                const isAutomated =
                  message.type === 'automated' ||
                  message.type?.includes('automated');

                // Render automated/system messages differently with link support
                if (isAutomated) {
                  const messageContent = (
                    <div className={styles.automatedMessage}>
                      <p
                        className={styles.automatedMessageText}
                        dangerouslySetInnerHTML={{ __html: message.text }}
                      />
                      <p className={styles.automatedMessageTimestamp}>
                        {moment(message.timestamp).format('h:mm A')}
                      </p>
                    </div>
                  );

                  // Handle specific automated message types with links
                  if (message.type === 'automated_bill') {
                    const linkTo = message.is_mine ? '/manage/pitches' : '/ats';
                    return (
                      <div
                        key={message.id}
                        className={styles.automatedMessageWrapper}
                      >
                        <Link to={linkTo}>{messageContent}</Link>
                      </div>
                    );
                  }

                  if (message.type === 'automated_timeular') {
                    return (
                      <div
                        key={message.id}
                        className={styles.automatedMessageWrapper}
                      >
                        <a
                          href="https://timeular.com/time-tracking-for-freelancers"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {messageContent}
                        </a>
                      </div>
                    );
                  }

                  if (message.type === 'automated_noty.ai') {
                    return (
                      <div
                        key={message.id}
                        className={styles.automatedMessageWrapper}
                      >
                        <a
                          href="https://www2.twine.net/NotyMessagesUpsell"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {messageContent}
                        </a>
                      </div>
                    );
                  }

                  if (message.type === 'automated_freelancer_toolkit') {
                    return (
                      <div
                        key={message.id}
                        className={styles.automatedMessageWrapper}
                      >
                        <a
                          href="https://www2.twine.net/FreelancerToolkitMessages"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {messageContent}
                        </a>
                      </div>
                    );
                  }

                  // Default automated message without link
                  return (
                    <div
                      key={message.id}
                      className={styles.automatedMessageWrapper}
                    >
                      {messageContent}
                    </div>
                  );
                }

                return (
                  <div
                    key={message.id}
                    className={`${styles.messageWrapper} ${
                      isMe
                        ? styles.messageWrapperRight
                        : styles.messageWrapperLeft
                    }`}
                  >
                    <div
                      className={`${styles.messageBubble} ${
                        isMe
                          ? styles.messageBubbleMe
                          : styles.messageBubbleOther
                      }`}
                    >
                      <p className={styles.messageText}>{message.text}</p>
                      <p className={styles.messageTimestamp}>
                        {moment(message.timestamp).format('h:mm A')}
                      </p>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))
        )}
      </div>

      {/* Message Input */}
      <div className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          <div className={styles.attachButtonWrapper}>
            <AttachFile onSendMessage={onSendFile} threadUser={otherUser} />
          </div>
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={handleMessageChange}
            onKeyPress={handleKeyPress}
            placeholder={`Message ${
              otherUser?.displayname?.split(' ')[0] ||
              otherUser?.username ||
              'applicant'
            }...`}
            className={styles.messageInput}
            rows={1}
          />
          <button
            onClick={() => handleSend()}
            disabled={!newMessage.trim()}
            className={styles.sendButton}
          >
            <Send width={18} height={18} />
            <span className={styles.sendButtonText}>Send</span>
          </button>
        </div>
      </div>
    </div>
  );
};

MessagesTab.propTypes = {
  messages: PropTypes.array,
  loading: PropTypes.bool,
  currentUser: PropTypes.object,
  otherUser: PropTypes.object,
  onSendMessage: PropTypes.func,
  onSendFile: PropTypes.func,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
};

MessagesTab.defaultProps = {
  messages: [],
  loading: false,
};

export default MessagesTab;
